import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { primaryColor, secondaryColor } from "../../../../utils/comon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight, faSpinner, faUpload, faTrash } from "@fortawesome/free-solid-svg-icons";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../common/formfields/CustomSelect";
import apiServices from "../../../../services/apiService";
import { uploadImageToAzure } from "../../../../services/AzureStorageService";
import { toast } from "react-toastify";

// Core priority config
const PRIORITIES = [
    { priority_id: 1, priority_name: "Low", priority_code: "LOW" },
    { priority_id: 2, priority_name: "Medium", priority_code: "MEDIUM" },
    { priority_id: 3, priority_name: "High", priority_code: "HIGH" },
    { priority_id: 4, priority_name: "Critical", priority_code: "CRITICAL" }
];

// Core categories config
const CATEGORIES = [
    { category_id: 1, category_name: "Electrical", isActiveId: 1 },
    { category_id: 2, category_name: "Plumbing", isActiveId: 1 },
    { category_id: 3, category_name: "Housekeeping", isActiveId: 1 },
    { category_id: 4, category_name: "IT / Network", isActiveId: 1 },
    { category_id: 5, category_name: "HVAC", isActiveId: 1 },
    { category_id: 6, category_name: "Other", isActiveId: 1 }
];

// Core assign roles config
const ASSIGN_ROLES = [
    { roleId: 1, roleName: "Zentech Admin" },
    { roleId: 2, roleName: "Super Admin" },
    { roleId: 3, roleName: "Site Admin" },
    { roleId: 4, roleName: "Unit Admin" },
    { roleId: 5, roleName: "Employee" },
    { roleId: 6, roleName: "Tenant Unit Admin" }
];

// Status configurations
const STATUS_OPTIONS = ["Open", "In Progress", "Close", "Reject"];

const ComplaintForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;

    const { user } = useSelector((state) => state.auth);
    const siteId = user?.siteId;
    const unitId = user?.branchorunitID;
    const roleId = Number(user?.roleId || user?.RoleId);
    const userId = user?.userId || user?.Id || user?.id || 1;

    const [form, setForm] = useState({
        title: "",
        description: "",
        categoryId: 1,
        priorityId: 1,
        assignToRoleId: 3,
        status: "Open",
        unit: "",
        remark: ""
    });

    const [errors, setErrors] = useState({});
    const [focusedField, setFocusedField] = useState("");
    
    const [selectedImages, setSelectedImages] = useState([]);
    const [deletedImages, setDeletedImages] = useState([]);

    useEffect(() => {
        if (!isEditMode && user) {
            setForm(prev => ({
                ...prev,
                unit: user.unitName || ""
            }));
        }
    }, [user, isEditMode]);

    useEffect(() => {
        if (isEditMode) {
            const fetchCurrentComplaint = async () => {
                try {
                    let url = `/CommComplaint/Complaint/getComplaints`;
                    const params = [];
                    if (roleId === 3) {
                        if (siteId) params.push(`siteId=${siteId}`);
                    } else if (roleId === 4) {
                        if (siteId) params.push(`siteId=${siteId}`);
                        if (unitId) params.push(`unitId=${unitId}`);
                    }
                    if (params.length > 0) {
                        url += `?${params.join("&")}`;
                    }

                    const response = await apiServices({
                        method: "GET",
                        url: url
                    });
                    if (response?.success) {
                        const list = response?.data?.data || response?.data || [];
                        const current = list.find(item => (item.complaintId || item.id) === Number(id));
                        if (current) {
                            setForm({
                                title: current.complaintTitle || current.title || "",
                                description: current.description || "",
                                categoryId: current.categoryId || 1,
                                priorityId: current.priorityId || 1,
                                assignToRoleId: current.assignedTo || current.assignToRoleId || 3,
                                status: current.status || "Open",
                                unit: current.unitName || current.unit || "",
                                remark: current.remark || ""
                            });

                            if (current.images && current.images.length > 0) {
                                setSelectedImages(current.images.map(img => ({
                                    url: img.image_url,
                                    isExisting: true
                                })));
                            } else if (current.problemImages && current.problemImages.length > 0) {
                                setSelectedImages(current.problemImages.map(url => ({
                                    url: url,
                                    isExisting: true
                                })));
                            }
                        }
                    }
                } catch (error) {
                    console.error("Failed to load complaint for editing:", error);
                }
            };
            fetchCurrentComplaint();
        }
    }, [id, isEditMode, siteId, unitId, roleId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: name.includes("Id") ? Number(value) : value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const validateForm = () => {
        const tempErrors = {};
        if (!form.title.trim()) tempErrors.title = "Title is required";
        if (!form.remark.trim()) tempErrors.remark = "Remark is required";
        if (!form.description.trim()) tempErrors.description = "Description is required";
        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleImageSelect = (e) => {
        const files = Array.from(e.target.files);
        const newImages = files.map(file => ({
            file,
            url: URL.createObjectURL(file),
            isExisting: false
        }));
        setSelectedImages(prev => [...prev, ...newImages]);
    };

    const handleRemoveImage = (indexToRemove) => {
        setSelectedImages(prev => {
            const updated = [...prev];
            const removed = updated.splice(indexToRemove, 1)[0];
            if (removed.isExisting && removed.url) {
                setDeletedImages(d => [...d, removed.url]);
            }
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!validateForm()) return;

        try {
            setIsSubmitting(true);
            
            // Upload new images to Azure first
            const newImageUrls = [];
            for (const img of selectedImages) {
                if (!img.isExisting && img.file) {
                    const uploadedUrl = await uploadImageToAzure(img.file);
                    if (uploadedUrl) {
                        newImageUrls.push(uploadedUrl);
                    }
                }
            }

            if (isEditMode) {
                const payload = {
                    complaintId: Number(id),
                    categoryId: form.categoryId,
                    priorityId: form.priorityId,
                    assignedTo: form.assignToRoleId,
                    complaintTitle: form.title,
                    description: form.description,
                    remark: form.remark,
                    status: form.status,
                    modifiedBy: String(userId),
                    newImages: newImageUrls,
                    deletedImages: deletedImages
                };

                const response = await apiServices({
                    method: "PUT",
                    url: `/CommComplaint/Complaint/updateComplaint`,
                    data: payload
                });

                if (response?.success) {
                    toast.success("Complaint updated successfully");
                    navigate("/zentcomm/complaint-management");
                } else {
                    toast.error(response?.message || "Failed to update complaint");
                }
            } else {
                const payload = {
                    siteId: siteId || 2,
                    unitId: unitId || 1,
                    userId: userId,
                    categoryId: form.categoryId,
                    priorityId: form.priorityId,
                    assignedTo: form.assignToRoleId,
                    complaintTitle: form.title,
                    description: form.description,
                    remark: form.remark,
                    status: form.status,
                    imageUrls: newImageUrls
                };

                const response = await apiServices({
                    method: "POST",
                    url: `/CommComplaint/Complaint/createComplaint`,
                    data: payload
                });

                if (response?.success) {
                    toast.success("Complaint raised successfully");
                    navigate("/zentcomm/complaint-management");
                } else {
                    toast.error(response?.message || "Failed to raise complaint");
                }
            }
        } catch (error) {
            console.error("Error submitting complaint form:", error);
            toast.error("An error occurred. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />

            <PageHeadingSection
                Navbar={true}
                mainHeading={isEditMode ? "Modify Complaint Details" : "Raise New Complaint"}
                subHeading={isEditMode ? "Update status, assignment, or detail records." : "Input the detailed issue below to notify maintenance team."}
            />

            <div className="mt-6 w-full max-w-5xl">
                <div className="bg-sectionheaderbg p-6 rounded-2xl border border-gray-100 dark:border-bordercolor flex flex-col gap-6 shadow-sm mb-4">
                    {/* Main Form Box */}
                    <form onSubmit={handleSubmit} className="flex flex-col gap-6">

                        {/* Section 1: Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="flex flex-col gap-2">
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="title">
                                    Complaint Title <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={form.title}
                                    onChange={handleInputChange}
                                    onFocus={() => setFocusedField("title")}
                                    onBlur={() => setFocusedField("")}
                                    placeholder="e.g. Water leak in lobby"
                                    className={`w-full h-10 px-4 bg-transparent text-textprimary border rounded-md text-sm font-medium transition-all placeholder:text-gray-400 focus:outline-none 
                                        ${errors.title ? "border-red-500" : "border-gray-200 dark:border-bordercolor hover:border-gray-400"}`}
                                    style={errors.title ? {} : (focusedField === "title" ? { borderColor: secondaryColor(), boxShadow: `0 0 0 2px ${secondaryColor()}40` } : {})}
                                />
                                {errors.title && <span className="text-[10px] text-red-500 font-bold mt-0.5">{errors.title}</span>}
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="remark">
                                    Remark <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="remark"
                                    name="remark"
                                    value={form.remark}
                                    onChange={handleInputChange}
                                    onFocus={() => setFocusedField("remark")}
                                    onBlur={() => setFocusedField("")}
                                    placeholder="e.g. Needs immediate attention"
                                    className={`w-full h-10 px-4 bg-transparent text-textprimary border rounded-md text-sm font-medium transition-all placeholder:text-gray-400 focus:outline-none 
                                        ${errors.remark ? "border-red-500" : "border-gray-200 dark:border-bordercolor hover:border-gray-400"}`}
                                    style={errors.remark ? {} : (focusedField === "remark" ? { borderColor: secondaryColor(), boxShadow: `0 0 0 2px ${secondaryColor()}40` } : {})}
                                />
                                {errors.remark && <span className="text-[10px] text-red-500 font-bold mt-0.5">{errors.remark}</span>}
                            </div>
                        </div>

                        {/* Section 2: Status & Assignment */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="flex flex-col gap-2">
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Assign To Role <span className="text-red-500">*</span>
                                </label>
                                <CustomSelect
                                    options={ASSIGN_ROLES.map(r => ({ label: r.roleName, value: r.roleId }))}
                                    value={ASSIGN_ROLES.find(r => r.roleId === form.assignToRoleId)
                                        ? { label: ASSIGN_ROLES.find(r => r.roleId === form.assignToRoleId).roleName, value: form.assignToRoleId }
                                        : null}
                                    onChange={(opt) => setForm(prev => ({ ...prev, assignToRoleId: opt ? opt.value : 3 }))}
                                    placeholder="Select Role"
                                />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Status <span className="text-red-500">*</span>
                                </label>
                                <CustomSelect
                                    options={STATUS_OPTIONS.map(s => ({ label: s, value: s }))}
                                    value={form.status ? { label: form.status, value: form.status } : null}
                                    onChange={(opt) => setForm(prev => ({ ...prev, status: opt ? opt.value : "Open" }))}
                                    placeholder="Select Status"
                                />
                            </div>
                        </div>

                        {/* Section 3: Category & Priority */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="flex flex-col gap-2">
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Category <span className="text-red-500">*</span>
                                </label>
                                <CustomSelect
                                    options={CATEGORIES.map(c => ({ label: c.category_name, value: c.category_id }))}
                                    value={CATEGORIES.find(c => c.category_id === form.categoryId)
                                        ? { label: CATEGORIES.find(c => c.category_id === form.categoryId).category_name, value: form.categoryId }
                                        : null}
                                    onChange={(opt) => setForm(prev => ({ ...prev, categoryId: opt ? opt.value : 1 }))}
                                    placeholder="Select Category"
                                />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Priority <span className="text-red-500">*</span>
                                </label>
                                <CustomSelect
                                    options={PRIORITIES.map(p => ({ label: p.priority_name, value: p.priority_id }))}
                                    value={PRIORITIES.find(p => p.priority_id === form.priorityId)
                                        ? { label: PRIORITIES.find(p => p.priority_id === form.priorityId).priority_name, value: form.priorityId }
                                        : null}
                                    onChange={(opt) => setForm(prev => ({ ...prev, priorityId: opt ? opt.value : 1 }))}
                                    placeholder="Select Priority"
                                />
                            </div>
                        </div>

                        {/* Section 4: Detailed Description */}
                        <div className="flex flex-col gap-2">
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="description">
                                Description <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                rows={5}
                                value={form.description}
                                onChange={handleInputChange}
                                onFocus={() => setFocusedField("description")}
                                onBlur={() => setFocusedField("")}
                                placeholder="Describe the issue in details..."
                                className={`w-full px-4 py-3 bg-transparent text-textprimary border rounded-md text-sm font-medium transition-all placeholder:text-gray-400 resize-none focus:outline-none 
                                    ${errors.description ? "border-red-500" : "border-gray-200 dark:border-bordercolor hover:border-gray-400"}`}
                                style={errors.description ? {} : (focusedField === "description" ? { borderColor: secondaryColor(), boxShadow: `0 0 0 2px ${secondaryColor()}40` } : {})}
                            />
                            {errors.description && <span className="text-[10px] text-red-500 font-bold mt-0.5">{errors.description}</span>}
                        </div>

                        {/* Section 5: Image Upload */}
                        <div className="flex flex-col gap-2">
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                Upload Images
                            </label>
                            
                            <div className="border-2 border-dashed border-gray-200 dark:border-bordercolor rounded-xl p-6 flex flex-col items-center justify-center bg-gray-50/50 dark:bg-actionitembg hover:bg-gray-50 dark:hover:bg-[#2B2B2D] transition-colors relative cursor-pointer">
                                <input 
                                    type="file" 
                                    multiple 
                                    accept="image/*" 
                                    onChange={handleImageSelect} 
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    disabled={isSubmitting}
                                />
                                <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/30 text-blue-500 rounded-full flex items-center justify-center mb-3">
                                    <FontAwesomeIcon icon={faUpload} />
                                </div>
                                <span className="text-sm font-semibold text-gray-700 dark:text-textprimary">Click or drag images to upload</span>
                                <span className="text-xs text-gray-400 dark:text-textsecondary mt-1">PNG, JPG, JPEG up to 5MB</span>
                            </div>

                            {/* Image Previews */}
                            {selectedImages.length > 0 && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-4">
                                    {selectedImages.map((img, index) => (
                                        <div key={index} className="relative group rounded-lg overflow-hidden border border-gray-200 dark:border-bordercolor aspect-square bg-gray-100 dark:bg-actionitembg">
                                            <img src={img.url} alt={`preview ${index}`} className="w-full h-full object-cover" />
                                            <button 
                                                type="button"
                                                onClick={() => handleRemoveImage(index)}
                                                className="absolute top-2 right-2 w-7 h-7 bg-white/90 dark:bg-black/50 text-red-500 hover:text-red-600 rounded-full flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                                disabled={isSubmitting}
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="text-xs" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Actions buttons */}

                    </form>
                </div>

                <div className="flex justify-between mt-auto border-t dark:border-bordercolor pt-4">
                    <button
                        type="button"
                        onClick={() => navigate("/zentcomm/complaint-management")}
                        className="px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textsecondary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors"
                    >
                        <FontAwesomeIcon icon={faAngleLeft} />
                        Cancel
                    </button>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        onClick={handleSubmit}
                        className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: "var(--color-secondaryaccent)", color: "var(--color-primaryaccent)" }}
                        onMouseEnter={(e) => {
                            if (!isSubmitting) {
                                e.currentTarget.style.backgroundColor = "var(--color-primaryaccent)";
                                e.currentTarget.style.color = '#FFFFFF';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isSubmitting) {
                                e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
                                e.currentTarget.style.color = "var(--color-primaryaccent)";
                            }
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                                Processing...
                            </>
                        ) : (
                            <>
                                {isEditMode ? "Save Complaint" : "Create Complaint"}
                                <FontAwesomeIcon icon={faAngleRight} />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ComplaintForm;
